import { useState, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import TablaEstandar from './TablaEstandar';
import BarraBusqueda from './BarraBusqueda';
import useTablaEstandar from './useTablaEstandar';
import { filtrarFilas, ordenarFilas, facetasDe } from './tablaLocal';
import { descargar, mensajeError } from './descargas';
import api from './tablaServidorApi';

/**
 * La tabla estándar para datos que ya están en el panel (una selección dentro de un formulario,
 * un listado que no viene paginado): búsqueda, orden y filtros por columna, columnas, densidad,
 * Excel y, opcionalmente, casillas de selección múltiple.
 *
 * `seleccion` = { estaSeleccionada(fila), alternar(fila), alternarTodas(filasVisibles, marcar) }.
 * Con ella, pulsar una fila (o su casilla) la marca. Las preferencias se guardan por usuario
 * con `tablaKey` (debe estar en TABLAS del back).
 */
const TablaLocal = ({
  tablaKey,
  columnas,
  filas,
  nombreEntidad,
  archivo = undefined,
  placeholder = 'Buscar…',
  camposBusqueda = undefined,
  seleccion = undefined,
  barraExtra = null,
  onFilaClick = undefined,
  renderCelda = undefined,
  tituloTarjeta,
  claveFila,
}) => {
  const tabla = useTablaEstandar({ tabla: tablaKey, columnas });
  const [busqueda, setBusqueda] = useState('');
  const [exportando, setExportando] = useState(false);
  const [errorExcel, setErrorExcel] = useState(null);

  const campos = useMemo(
    () => camposBusqueda || columnas.filter((c) => !c.fixed).map((c) => c.id),
    [camposBusqueda, columnas],
  );
  const visibles = useMemo(
    () =>
      ordenarFilas(
        filtrarFilas(filas, tabla.filtros, busqueda, campos),
        tabla.sortBy,
        tabla.sortDir,
      ),
    [filas, tabla.filtros, busqueda, campos, tabla.sortBy, tabla.sortDir],
  );

  const loadFacets = useCallback(
    async (columna) => facetasDe(filas, columna, tabla.filtros, busqueda, campos),
    [filas, tabla.filtros, busqueda, campos],
  );

  const exportar = async () => {
    setExportando(true);
    setErrorExcel(null);
    try {
      const cols = tabla.columnasVisibles
        .filter((c) => !c.fixed)
        .map((c) => ({ id: c.id, label: c.label }));
      const blob = await api.excelLocal(archivo || tablaKey, cols, visibles);
      descargar(blob, `${archivo || tablaKey}-${new Date().toISOString().slice(0, 10)}.xlsx`);
    } catch (err) {
      setErrorExcel(await mensajeError(err));
    } finally {
      setExportando(false);
    }
  };

  const celda = (col, fila) => {
    const propia = renderCelda?.(col, fila);
    return propia !== undefined ? propia : fila[col.id];
  };

  return (
    <>
      <BarraBusqueda
        busqueda={busqueda}
        onChange={setBusqueda}
        placeholder={placeholder}
        nombreEntidad={nombreEntidad}
        barraExtra={barraExtra}
      />
      {errorExcel && (
        <div role="alert" className="alert alert-danger" style={{ marginTop: '12px' }}>
          {errorExcel}
        </div>
      )}
      {tabla.ready && (
        <TablaEstandar
          tabla={tabla}
          filas={visibles}
          total={visibles.length}
          onFilaClick={onFilaClick}
          loadFacets={loadFacets}
          onExportar={exportar}
          exportando={exportando}
          renderCelda={celda}
          tituloTarjeta={tituloTarjeta}
          nombreEntidad={nombreEntidad}
          claveFila={claveFila}
          seleccion={seleccion}
        />
      )}
    </>
  );
};

TablaLocal.propTypes = {
  tablaKey: PropTypes.string.isRequired,
  columnas: PropTypes.array.isRequired,
  filas: PropTypes.array.isRequired,
  nombreEntidad: PropTypes.string.isRequired,
  archivo: PropTypes.string,
  placeholder: PropTypes.string,
  camposBusqueda: PropTypes.array,
  seleccion: PropTypes.object,
  barraExtra: PropTypes.node,
  onFilaClick: PropTypes.func,
  renderCelda: PropTypes.func,
  tituloTarjeta: PropTypes.func.isRequired,
  claveFila: PropTypes.func.isRequired,
};

export default TablaLocal;
